/**
 * Root React component with providers and routing.
 *
 * - QueryClientProvider: TanStack Query for server state.
 * - RouterProvider: React Router with app routes.
 */

import { AuthGuard } from "@frontend/components/auth/AuthGuard"
import { AuthRuntimeProvider, useAuthRuntime } from "@frontend/components/auth/auth-runtime"
import { AppLayout } from "@frontend/components/layout/AppLayout"
import { RouteErrorBoundary } from "@frontend/components/route-error-boundary"
import { Toaster } from "@frontend/components/ui/sonner"
import { TooltipProvider } from "@frontend/components/ui/tooltip"
import { useApiQuery } from "@frontend/hooks/use-api"
import { AccessDenied } from "@frontend/pages/access-denied/page"
import { CaseWorkspacePage } from "@frontend/pages/case-workspace/page"
import { InstanceMembers } from "@frontend/pages/instance-members/page"
import { ModuleTemplatesPage } from "@frontend/pages/module-templates/page"
import { NoAccess } from "@frontend/pages/no-access/page"
import { ProjectMembersContainer } from "@frontend/pages/project-detail/components/project-members"
import { ProjectDetail } from "@frontend/pages/project-detail/page"
import { ProjectForm } from "@frontend/pages/project-form/page"
import { ProjectListContainer } from "@frontend/pages/projects/page"
import { SettingsPage } from "@frontend/pages/settings/page"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { type ReactNode, useEffect } from "react"
import { createBrowserRouter, Link, RouterProvider, useNavigate } from "react-router"
import "./index.css"

// --- Query Client ---

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000 }
  }
})

function NotFound() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold">404</h1>
        <p className="text-muted-foreground">Page not found.</p>
        <Link to="/" className="text-primary hover:underline">
          Return to Dashboard
        </Link>
      </div>
    </div>
  )
}

function AuthCallback() {
  const navigate = useNavigate()
  const { isLoading, isAuthenticated, error } = useAuthRuntime()

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/", { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate])

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <div className="max-w-xl text-center space-y-2">
          <p className="text-sm text-destructive">Auth0 callback failed.</p>
          <p className="text-xs text-muted-foreground break-words">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-6">
      <p className="text-sm text-muted-foreground">Completing sign-in...</p>
    </div>
  )
}

function InstanceAdminOnly({ children }: { children: ReactNode }) {
  const { data: currentUser, isLoading } = useApiQuery<{
    id: string
    instance_role: "admin" | "member" | null
  }>(["auth", "me"], "/auth/me")

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-muted-foreground">Checking permissions...</p>
      </div>
    )
  }

  if (currentUser?.instance_role !== "admin") {
    return <AccessDenied />
  }

  return <>{children}</>
}

// --- Router ---

const router = createBrowserRouter([
  {
    path: "/auth/callback",
    element: <AuthCallback />,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/projects/:projectId/cases/:caseId",
    element: (
      <AuthGuard>
        <CaseWorkspacePage />
      </AuthGuard>
    ),
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/projects/:projectId/cases/:caseId/review/:submissionId",
    element: (
      <AuthGuard>
        <CaseWorkspacePage />
      </AuthGuard>
    ),
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/",
    element: (
      <AuthGuard>
        <AppLayout />
      </AuthGuard>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <ProjectListContainer /> },
      {
        path: "members",
        element: (
          <InstanceAdminOnly>
            <InstanceMembers />
          </InstanceAdminOnly>
        )
      },
      {
        path: "templates",
        element: (
          <InstanceAdminOnly>
            <ModuleTemplatesPage />
          </InstanceAdminOnly>
        )
      },
      { path: "projects/new", element: <ProjectForm /> },
      { path: "projects/:projectId", element: <ProjectDetail /> },
      { path: "projects/:projectId/edit", element: <ProjectForm /> },
      { path: "projects/:projectId/members", element: <ProjectMembersContainer /> },
      { path: "settings", element: <SettingsPage /> },
      { path: "no-access", element: <NoAccess /> },
      { path: "access-denied", element: <AccessDenied /> },
      { path: "*", element: <NotFound /> }
    ]
  }
])

// --- App ---

function AppWithProviders() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthRuntimeProvider>
        <TooltipProvider>
          <RouterProvider router={router} />
          <Toaster position="top-right" />
        </TooltipProvider>
      </AuthRuntimeProvider>
    </QueryClientProvider>
  )
}

export function App() {
  return <AppWithProviders />
}

export default App
